import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from "@/components/ui/pagination";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { DataTableFooter } from "./data-table-footer";
import { getPageNumbers } from "./pagination-utils";

type DataTablePagerProps = {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: ReadonlyArray<number>;
  className?: string;
};

const DEFAULT_PAGE_SIZES = [10, 20, 30, 40, 50];

/** HRMS 分页布局与页码窗口，保留当前项目的按钮基础样式。 */
export function DataTablePager({
  page,
  pageCount,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className,
}: DataTablePagerProps) {
  const { intlLocale, t } = useI18n();
  const numberFormatter = new Intl.NumberFormat(intlLocale);
  const totalPages = Math.max(1, pageCount);
  const currentPage = Math.max(1, Math.min(page, totalPages));
  const canChangePageSize = pageSize !== undefined && onPageSizeChange !== undefined;
  const availablePageSizes = canChangePageSize ? [...new Set([pageSize, ...pageSizeOptions])].sort((a, b) => a - b) : [];
  if (totalPages <= 1 && !canChangePageSize) return null;

  return (
    <div className={cn("flex flex-wrap items-center justify-end gap-3", className)}>
      {canChangePageSize ? (
        <div className="flex items-center gap-2">
          <span>{t("dataTable.pagination.rowsPerPage")}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              const next = Number(value);
              if (availablePageSizes.includes(next) && next !== pageSize) onPageSizeChange(next);
            }}
          >
            <SelectTrigger aria-label={t("dataTable.pagination.rowsPerPage")} className="h-7 w-auto" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {availablePageSizes.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {numberFormatter.format(size)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {totalPages > 1 ? (
        <Pagination aria-label={t("dataTable.pagination.label")} className="mx-0 w-auto justify-end">
          <PaginationContent>
            <PaginationItem>
              <Button
                aria-label={t("dataTable.pagination.previous")}
                disabled={currentPage <= 1}
                size="icon-sm"
                variant="ghost"
                onClick={() => onPageChange(currentPage - 1)}
              >
                <ChevronLeftIcon />
              </Button>
            </PaginationItem>
            {getPageNumbers(currentPage, totalPages).map((entry, index) =>
              entry === "..." ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: 省略号的位置由当前页码窗口决定。
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={entry}>
                  <Button
                    aria-current={entry === currentPage ? "page" : undefined}
                    aria-label={t("dataTable.pagination.page", { page: numberFormatter.format(entry) })}
                    size="icon-sm"
                    variant={entry === currentPage ? "secondary" : "ghost"}
                    onClick={() => onPageChange(entry)}
                  >
                    {numberFormatter.format(entry)}
                  </Button>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <Button
                aria-label={t("dataTable.pagination.next")}
                disabled={currentPage >= totalPages}
                size="icon-sm"
                variant="ghost"
                onClick={() => onPageChange(currentPage + 1)}
              >
                <ChevronRightIcon />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
}

type DataTablePaginationProps = Omit<DataTablePagerProps, "pageCount"> & {
  totalPages: number;
  totalCount?: number;
  itemLabel?: string;
};

/** 兼容原有服务端分页 API，统计与页码保持在表格滚动区之外。 */
export function DataTablePagination({ totalPages, totalCount, itemLabel, className, ...props }: DataTablePaginationProps) {
  const { intlLocale, t } = useI18n();
  if (totalPages <= 1 && totalCount === undefined && !props.onPageSizeChange) return null;

  return (
    <DataTableFooter
      className={cn("pt-3", className)}
      summary={
        totalCount === undefined
          ? undefined
          : t("dataTable.pagination.total", {
              count: new Intl.NumberFormat(intlLocale).format(totalCount),
              itemLabel: itemLabel ?? t("dataTable.pagination.records"),
            })
      }
    >
      <DataTablePager {...props} pageCount={totalPages} />
    </DataTableFooter>
  );
}
